import { arrayExtent } from './extent.js';
const MAP_WIDTH = 560;
const MAP_HEIGHT = 300;
const TRACK_PLOT_WIDTH = 400;
const MAP_PADDING = 26;
const CALLOUT_X = 420;
const CALLOUT_SLOTS = [55, 150, 245];
const MAX_TRACE_POINTS = 700;

const HIGHLIGHT_COLORS = ['#fbbf24', '#f472b6', '#fb7185'];

function validTracePoints(trace) {
  return (trace?.points || []).filter(point =>
    Number.isFinite(point.lat)
    && Number.isFinite(point.lon)
    && Number.isFinite(point.dist_m));
}

export function pickDebriefMapTrace(lapTraces, selectedLaps) {
  const selected = (selectedLaps || [])
    .filter(index => Number(index) >= 0)
    .map(index => lapTraces?.[index])
    .filter(trace => validTracePoints(trace).length >= 4);
  const fallback = Object.entries(lapTraces || {})
    .filter(([index, trace]) => Number(index) >= 0 && validTracePoints(trace).length >= 4)
    .map(([, trace]) => trace);
  const candidates = selected.length ? selected : fallback;
  return candidates.reduce((best, trace) => {
    if (!best) return trace;
    const time = trace.lap_time_seconds ?? Number.POSITIVE_INFINITY;
    const bestTime = best.lap_time_seconds ?? Number.POSITIVE_INFINITY;
    return time < bestTime ? trace : best;
  }, null);
}

function downsample(points) {
  if (points.length <= MAX_TRACE_POINTS) return points;
  const stride = Math.ceil(points.length / MAX_TRACE_POINTS);
  const sampled = points.filter((_, index) => index % stride === 0);
  const last = points[points.length - 1];
  if (sampled[sampled.length - 1] !== last) sampled.push(last);
  return sampled;
}

export function projectDebriefTrack(points, width = MAP_WIDTH, height = MAP_HEIGHT,
                                     padding = MAP_PADDING) {
  if (!points?.length) return [];
  const meanLat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const cosLat = Math.cos(meanLat * Math.PI / 180);
  const raw = points.map(point => ({
    x: point.lon * cosLat,
    y: -point.lat,
    distM: point.dist_m,
  }));
  const xs = raw.map(point => point.x);
  const ys = raw.map(point => point.y);
  const [minX, maxX] = arrayExtent(xs);
  const [minY, maxY] = arrayExtent(ys);
  const rangeX = Math.max(maxX - minX, 1e-9);
  const rangeY = Math.max(maxY - minY, 1e-9);
  const scale = Math.min((width - padding * 2) / rangeX, (height - padding * 2) / rangeY);
  const drawnWidth = rangeX * scale;
  const drawnHeight = rangeY * scale;
  const offsetX = (width - drawnWidth) / 2;
  const offsetY = (height - drawnHeight) / 2;
  return raw.map(point => ({
    x: offsetX + (point.x - minX) * scale,
    y: offsetY + (point.y - minY) * scale,
    distM: point.distM,
  }));
}

function svgPath(points) {
  return points.map((point, index) =>
    `${index ? 'L' : 'M'}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(' ');
}

function rangePoints(points, startM, endM) {
  if (!points.length) return [];
  let first = points.findIndex(point => point.distM >= startM);
  let last = -1;
  for (let index = points.length - 1; index >= 0; index -= 1) {
    if (points[index].distM <= endM) { last = index; break; }
  }
  if (first < 0 || last < first) {
    const midpoint = (startM + endM) / 2;
    const nearest = points.reduce((best, point, index) =>
      Math.abs(point.distM - midpoint) < best.error
        ? { index, error: Math.abs(point.distM - midpoint) } : best,
    { index: 0, error: Number.POSITIVE_INFINITY }).index;
    first = nearest;
    last = nearest;
  }
  return points.slice(Math.max(0, first - 1), Math.min(points.length, last + 2));
}

function nearestApexDistance(points, corner) {
  const lat = corner?.apex_lat ?? corner?.lat;
  const lon = corner?.apex_lon ?? corner?.lon;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const cosLat = Math.cos(lat * Math.PI / 180);
  return points.reduce((best, point) => {
    const error = (point.lat - lat) ** 2 + ((point.lon - lon) * cosLat) ** 2;
    return error < best.error ? { error, distM: point.dist_m } : best;
  }, { error: Number.POSITIVE_INFINITY, distM: null }).distM;
}

function matchingSector(sectors, gap) {
  return (sectors || []).find(sector =>
    String(sector.id) === String(gap.sector_id)
    || (sector.label && sector.label === gap.label));
}

function matchingCorner(corners, gap) {
  return (corners || []).find(corner =>
    String(corner.id) === String(gap.corner_id)
    || (corner.label && corner.label === gap.label));
}

export function buildDebriefTrackMap({
  lapTraces,
  selectedLaps,
  corners,
  sectors,
  gaps,
  sectorGaps,
}) {
  const trace = pickDebriefMapTrace(lapTraces, selectedLaps);
  const rawPoints = validTracePoints(trace);
  if (rawPoints.length < 4) return null;
  const totalDistM = rawPoints.reduce((max, point) => Math.max(max, point.dist_m), 0);
  if (!(totalDistM > 0)) return null;

  const sampledRaw = downsample(rawPoints);
  const projected = projectDebriefTrack(sampledRaw, TRACK_PLOT_WIDTH, MAP_HEIGHT);
  const highlightRanges = [];

  for (const gap of sectorGaps || []) {
    const sector = matchingSector(sectors, gap);
    const startFrac = gap.start_frac != null ? Number(gap.start_frac) : Number(sector?.start_frac);
    const endFrac = gap.end_frac != null ? Number(gap.end_frac) : Number(sector?.end_frac);
    const startM = Math.max(0, startFrac * totalDistM);
    const endM = Math.min(totalDistM, endFrac * totalDistM);
    if (!Number.isFinite(startM) || !Number.isFinite(endM) || endM <= startM) continue;
    highlightRanges.push({
      id: `sector-${gap.sector_id}`,
      label: gap.label || sector?.label || String(gap.sector_id),
      startM,
      endM,
      kind: 'sector',
    });
  }

  if (!highlightRanges.length) {
    for (const gap of gaps || []) {
      const corner = matchingCorner(corners, gap);
      const apexM = nearestApexDistance(rawPoints, corner);
      if (!corner || apexM == null) continue;
      const entrySpan = corner.entry_dist_m > 0 ? corner.entry_dist_m : 100;
      const exitSpan = corner.exit_dist_m > 0 ? corner.exit_dist_m : 100;
      highlightRanges.push({
        id: `corner-${gap.corner_id}`,
        label: gap.label || corner.label || String(gap.corner_id),
        startM: Math.max(0, apexM - entrySpan),
        endM: Math.min(totalDistM, apexM + exitSpan),
        kind: 'corner',
      });
    }
  }

  const highlights = highlightRanges.slice(0, 3).map((range, index) => {
    const points = rangePoints(projected, range.startM, range.endM);
    if (points.length < 2) return null;
    const marker = points[Math.floor(points.length / 2)];
    return {
      ...range,
      index: index + 1,
      color: HIGHLIGHT_COLORS[index % HIGHLIGHT_COLORS.length],
      path: svgPath(points),
      marker,
    };
  }).filter(Boolean);
  if (!highlights.length) return null;

  const spatialOrder = [...highlights].sort((a, b) => a.marker.y - b.marker.y);
  spatialOrder.forEach((highlight, slotIndex) => {
    highlight.callout = {
      x: CALLOUT_X,
      y: CALLOUT_SLOTS[slotIndex],
    };
  });

  return {
    width: MAP_WIDTH,
    height: MAP_HEIGHT,
    basePath: svgPath(projected),
    highlights,
    evidenceKind: highlights[0].kind,
  };
}
